import Axios, {AxiosResponse} from 'axios';
import WebConnector from '../WebConnector/WebConnector';
import Constants from '../../globals/Constants';
import AxiosCancelTokenHelper from '../../utils/AxiosCancelTokenHelper/AxiosCancelTokenHelper';
import Config from '../../globals/Config';
import Store from '../../redux/Store';
import Log from '../../utils/Log';

const makeCallWithTscAuthToken = async (callFn: (token: string) => Promise<AxiosResponse>): Promise<AxiosResponse> => {
   try {
      const token = await WebConnector.getToken();
      return await callFn(token);
   } catch (e) {
      if (e.response && e.response.status === Constants.statusCodes.unauthorized) {
         const token = await WebConnector.refreshToken();
         return await callFn(token);
      }
      throw e;
   }
};

const getHeaders = (token: string): any => {
   if (Config.webConnector.common.initialAuthResponse.UserInfoToken) {
      return {Authorization: `Bearer ${token}`, 'Auth-Proxy-User-Info': Config.webConnector.common.initialAuthResponse.UserInfoToken};
   }
   return {Authorization: `Bearer ${token}`};
};

const postWithTscAuthToken = (url: string, requestBody?: any): Promise<AxiosResponse> => {
   return makeCallWithTscAuthToken(token => {
      return Axios.post(url, requestBody, {headers: getHeaders(token)});
   });
};

const putWithTscAuthToken = (url: string, requestBody?: any): Promise<AxiosResponse> => {
   return makeCallWithTscAuthToken(token => {
      return Axios.put(url, requestBody, {headers: getHeaders(token)});
   });
};

const getWithTscAuthToken = (url: string, axiosCancelTokenHelper?: AxiosCancelTokenHelper): Promise<AxiosResponse> => {
   return makeCallWithTscAuthToken(token => {
      return axiosCancelTokenHelper ?
         Axios.get(url, {headers: getHeaders(token), cancelToken: axiosCancelTokenHelper.getToken()}) :
         Axios.get(url, {headers: getHeaders(token)});
   });
};

const deleteWithTscAuthToken = (url: string): Promise<AxiosResponse> => {
   return makeCallWithTscAuthToken(token => {
      return Axios.delete(url, {headers: getHeaders(token)});
   });
};

const getWithBasicAuth = (url: string, userId: string, userSecret: string, axiosCancelTokenHelper?: AxiosCancelTokenHelper): Promise<AxiosResponse> => {
   return Axios.get(url, {
      auth: {
         username: userId,
         password: userSecret
      }, ...(axiosCancelTokenHelper && {cancelToken: axiosCancelTokenHelper.getToken()})
   });
};

const postWithBasicAuth = (url: string, userId: string, userSecret: string, requestBody?: any): Promise<AxiosResponse> => {
   return Axios.post(url, requestBody, {
      auth: {
         username: userId,
         password: userSecret
      }
   });
};

const putWithBasicAuth = (url: string, requestBody: any, userId: string, userSecret: string): Promise<AxiosResponse> => {
   return Axios.put(url, requestBody, {
      auth: {
         username: userId,
         password: userSecret
      }
   });
};

const deleteWithBasicAuth = (url: string, userId: string, userSecret: string): Promise<AxiosResponse> => {
   return Axios.delete(url, {
      auth: {
         username: userId,
         password: userSecret
      }
   });
};

const getWithMultipleAuths = (url: string, axiosCancelTokenHelper?: AxiosCancelTokenHelper): Promise<AxiosResponse> => {
   const userId: string = Store.getState().currentUserId;
   const secret: string = Config.user.secret;
   if (Config.user.isAuthenticated) {
      return getWithTscAuthToken(url, axiosCancelTokenHelper);
   } else if (userId && secret) {
      return getWithBasicAuth(url, userId, secret, axiosCancelTokenHelper);
   } else {
      return Axios.get(url, {...(axiosCancelTokenHelper && {cancelToken: axiosCancelTokenHelper.getToken()})});
   }
};

const postWithMultipleAuths = (url: string, requestBody?: any): Promise<AxiosResponse> => {
   const userId: string = Store.getState().currentUserId;
   const secret: string = Config.user.secret;
   if (Config.user.isAuthenticated) {
      return postWithTscAuthToken(url, requestBody);
   } else if (userId && secret) {
      return postWithBasicAuth(url, userId, secret, requestBody);
   } else {
      return Axios.post(url, requestBody);
   }
};

const putWithMultipleAuths = (url: string, requestBody: any): Promise<AxiosResponse> => {
   const userId: string = Store.getState().currentUserId;
   const secret: string = Config.user.secret;
   if (Config.user.isAuthenticated) {
      return putWithTscAuthToken(url, requestBody);
   } else if (userId && secret) {
      return putWithBasicAuth(url, requestBody, userId, secret);
   } else {
      return Axios.put(url, requestBody);
   }
};

const deleteWithMultipleAuths = (url: string): Promise<AxiosResponse> => {
   const userId: string = Store.getState().currentUserId;
   const secret: string = Config.user.secret;
   if (Config.user.isAuthenticated) {
      return deleteWithTscAuthToken(url);
   } else if (userId && secret) {
      return deleteWithBasicAuth(url, userId, secret);
   } else {
      return Axios.delete(url);
   }
};

const executeRequestWithErrorHandling = async (request: () => Promise<AxiosResponse>, methodName: string, expectedStatusCode: number, axiosCancelTokenHelper?: AxiosCancelTokenHelper): Promise<AxiosResponse> => {
   try {
      const response = await request();
      if (response.status !== expectedStatusCode) {
         const failureMessage = `${methodName} failed with status code ${response.status}`;
         Log.warn(failureMessage);
         throw {status: response.status, message: failureMessage};
      }
      return response;
   } catch (error) {
      let errorReason = '';
      if (!axiosCancelTokenHelper || !axiosCancelTokenHelper.isCancelled) {
         let logMessage = `${methodName} failed. ${error}`;
         errorReason = error && error.response && error.response.data && error.response.data.Reason || '';
         if (errorReason) {
            logMessage += `. Reason: ${error.response.data.Reason}`;
         }

         Log.warn(logMessage);
      }

      if (error && error.response && error.response.status) {
         throw {status: error.response.status, message: error.message, reason: errorReason};
      }

      throw error;
   }
};

export default {
   postWithTscAuthToken,
   getWithTscAuthToken,
   putWithTscAuthToken,
   deleteWithTscAuthToken,
   getWithMultipleAuths,
   postWithMultipleAuths,
   putWithMultipleAuths,
   deleteWithMultipleAuths,
   executeRequestWithErrorHandling
};
